import BaseAPI from './base.api';

export interface TireData {
  customer: string;
  device: string;
  tire: string;
  side_of_truck: string;
  bearing: number;
  start_ts: string;
  end_ts: string;
  pressure_start: string;
  pressure_end: string;
  pressure_min: string;
  pressure_min_ts: string;
  gpslatitude: number;
  gpslongitude: number;
  avg_fuel_rate?: number;
  avg_speed?: number;
  // Asset mapping fields (added from device-to-asset mapping)
  assetId?: string; // Asset display name (bumper number or asset ID)
  deviceId?: string; // Original device ID
  bumperNumber?: string; // Asset bumper number
  assetVin?: string; // Asset VIN
  // Legacy fields for backward compatibility
  tireName?: string;
  tireCode?: string;
  sideOfTruck?: string;
  pressureValue?: number;
  timestamp?: string;
  isLeakageDetected?: boolean;
  leakageSeverity?: 'low' | 'medium' | 'high';
  leakageType?: 'fast' | 'slow';
}

export interface TireLeakageAnalysisResponse {
  success: boolean;
  data: {
    fast: TireData[];
    slow: TireData[];
  };
  totalRecords?: {
    fast: number;
    slow: number;
  };
  totalLeakages?: number;
  totalAssets?: number;
  analysisPeriod: {
    startDate: string;
    endDate: string;
  };
  unitSystem: string;
  pressureUnit: string;
}

// For internal use - transformed data structure
export interface AssetTireData {
  [tireKey: string]: TireData | null | number;
  totalLeakages: number;
}

export interface TireLeakageDataGrouped {
  time: string;
  assets: Record<string, AssetTireData>;
}

export interface TransformedTireLeakageResponse {
  success: boolean;
  data: TireLeakageDataGrouped[];
  totalLeakages: number;
  totalAssets: number;
  analysisPeriod: {
    startDate: string;
    endDate: string;
  };
}

export default class TireLeakageAPI extends BaseAPI {
  /**
   * Get tire leakage analysis data for a specific date range and customer
   * @param customerId - Customer ID
   * @param startDate - Start date in YYYY-MM-DD format
   * @param endDate - End date in YYYY-MM-DD format
   * @returns Promise<TireLeakageAnalysisResponse> - API response data
   */
  getAnalysis = async (
    customerId: string,
    startDate: string,
    endDate: string
  ): Promise<TireLeakageAnalysisResponse> => {
    const response = await this.API.get('tire-leakage/analysis', {
      params: {
        customerId,
        startDate,
        endDate,
      },
    });
    return response.data;
  };

  /**
   * Get GPS geopoints for a specific device and time range
   * @param deviceId - Device ID
   * @param startTimestamp - Start timestamp (YYYY-MM-DD HH:mm:ss.SSS format)
   * @param endTimestamp - End timestamp (YYYY-MM-DD HH:mm:ss.SSS format)
   * @returns Promise with GPS coordinates data
   */
  getGeopoints = async (
    deviceId: string,
    startTimestamp: string,
    endTimestamp: string
  ): Promise<any> => {
    const response = await this.API.get('tire-leakage/geopoints', {
      params: {
        deviceId,
        startTimestamp,
        endTimestamp,
      },
    });
    return response.data;
  };
}
